import { clearPermissionCache } from '../acl';
import { authAPI, type User } from '../api/authApi';
import { clearItemsCache } from '../api/itemsApi';
import { maintenanceAPI } from '../api/maintenanceApi';
import { clearRelationLabelCache } from '../component/TableHelper';
import { resolveApiBaseUrl } from '../api/apiBase';
import {
    IMPERSONATOR_TOKEN_KEY,
    IMPERSONATOR_USER_KEY,
    IMPERSONATED_UID_KEY,
    clearImpersonationStorage,
    readImpersonatorToken,
    readImpersonatorUserRaw,
    readImpersonatedUid,
    hasStoredImpersonationSession,
} from './impersonationStorage';

export { hasStoredImpersonationSession };

const API_BASE_URL = resolveApiBaseUrl();

function readStoredUser(): User | null {
    try {
        const raw = localStorage.getItem('user');
        return raw ? (JSON.parse(raw) as User) : null;
    } catch {
        return null;
    }
}

function userUid(user: User | null | undefined): string {
    if (!user) return '';
    const nested = user as { info?: { uid?: string }; userData?: { uid?: string } };
    return String(user.uid || user.id || nested.info?.uid || nested.userData?.uid || '').trim();
}

/** True when the current session is an admin impersonating another user. */
export function isImpersonating(currentUser?: User | null): boolean {
    if (!readImpersonatorToken()) {
        return false;
    }
    const targetUid = readImpersonatedUid();
    const currentId = userUid(currentUser ?? readStoredUser());
    // Legacy leftover keys (no target uid) are not a live impersonation session.
    if (!targetUid) {
        clearImpersonationStorage();
        return false;
    }
    if (currentId && currentId !== targetUid) {
        return false;
    }
    const impersonatorId = userUid(getImpersonatorUser());
    if (impersonatorId && currentId && impersonatorId === currentId) {
        return false;
    }
    return true;
}

/** The original admin account that started impersonating, if any. */
export function getImpersonatorUser(): User | null {
    try {
        const raw = readImpersonatorUserRaw();
        return raw ? (JSON.parse(raw) as User) : null;
    } catch {
        return null;
    }
}

/**
 * Persist the current (admin) session so it can be restored later. Only stores it
 * the first time, so impersonating a second user still returns to the real admin.
 */
function rememberImpersonatorSession(token: string, impersonatedUid: string): void {
    if (!readImpersonatorToken()) {
        localStorage.setItem(IMPERSONATOR_TOKEN_KEY, token);
        const adminUser = readStoredUser();
        if (adminUser) {
            localStorage.setItem(IMPERSONATOR_USER_KEY, JSON.stringify(adminUser));
        }
    }
    localStorage.setItem(IMPERSONATED_UID_KEY, impersonatedUid);
}

/**
 * Drop the persisted Redux store so the next boot reloads collections, menus and
 * permissions for the user we are switching to (the store is persisted with
 * `hardSet`, so stale config would otherwise rehydrate over the new user's).
 */
function clearPersistedConfig(): void {
    try {
        localStorage.removeItem('persist:root');
    } catch {
        // ignore storage errors
    }
}

export async function impersonateUserByUid(uid: string): Promise<void> {
    const targetUid = String(uid ?? '').trim();
    if (!targetUid) {
        throw new Error('Invalid user');
    }

    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) {
        throw new Error('Authentication token not found');
    }

    try {
        await maintenanceAPI.clearCache();
    } catch (cacheError) {
        console.error('Error clearing cache before impersonation:', cacheError);
    }

    const response = await fetch(`${API_BASE_URL}/auth/impersonate/${encodeURIComponent(targetUid)}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to impersonate user' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    const responseText = await response.text();
    let tokenValue: string | null = null;
    let userData: unknown = null;

    try {
        const data = JSON.parse(responseText) as { token?: string; idToken?: string; user?: User };
        tokenValue = data.token || data.idToken || (typeof data === 'string' ? data : null);
        userData = data.user;
    } catch {
        tokenValue = responseText.trim();
    }

    if (!tokenValue || typeof tokenValue !== 'string' || tokenValue.length === 0) {
        throw new Error('Invalid token response from server');
    }

    const u = userData as User | undefined;
    const userForStorage: User | undefined = u
        ? {
            uid: u.uid || (u as { id?: string }).id || '',
            email: u.email || '',
            active: u.active ?? true,
            admin: u.admin ?? false,
            role: u.role,
            firstName: u.firstName,
            lastName: u.lastName,
            organization: u.organization,
            info: {
                uid: u.uid || (u as { id?: string }).id || '',
                firstName: u.firstName,
                lastName: u.lastName,
                name:
                    [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                    u.email ||
                    u.uid ||
                    '',
                active: u.active ?? true,
                admin: u.admin,
                role: u.role,
            },
        }
        : undefined;

    const resolvedImpersonatedUid = userUid(userForStorage ?? null) || targetUid;
    // Remember the admin session before swapping tokens so we can return to it.
    rememberImpersonatorSession(token, resolvedImpersonatedUid);

    clearPermissionCache();
    clearItemsCache();
    clearRelationLabelCache();
    // Force the impersonated user's config to load fresh on reload.
    clearPersistedConfig();

    authAPI.loginWithToken(tokenValue, userForStorage);
    window.location.href = '/';
}

/**
 * Restore the original admin account after impersonating and reload the app so the
 * admin's config is fetched fresh.
 */
export async function exitImpersonation(): Promise<void> {
    const adminToken = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
    if (!adminToken) {
        throw new Error('Not currently impersonating');
    }
    const adminUser = getImpersonatorUser();

    try {
        await maintenanceAPI.clearCache();
    } catch (cacheError) {
        console.error('Error clearing cache before exiting impersonation:', cacheError);
    }

    clearImpersonationStorage();

    clearPermissionCache();
    clearItemsCache();
    clearRelationLabelCache();
    clearPersistedConfig();

    authAPI.loginWithToken(adminToken, adminUser);
    window.location.href = '/';
}
